import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../LanguageContext';

gsap.registerPlugin(ScrollTrigger);

const HeroScroll = () => {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const stickyRef = useRef(null);
  const planeRef = useRef(null);
  const skyRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const aboutLineRefs = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const plane = planeRef.current;
    const sky = skyRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const scrollIndicator = scrollIndicatorRef.current;
    const aboutLines = aboutLineRefs.current?.filter(Boolean) ?? [];

    const safeTarget = (target) => {
      if (!target) return null;
      if (Array.isArray(target)) {
        const filtered = target.filter(Boolean);
        return filtered.length ? filtered : null;
      }
      return target;
    };

    const safeSet = (target, vars) => {
      const t = safeTarget(target);
      if (!t) return;
      gsap.set(t, vars);
    };

    const safeTo = (target, vars, position) => {
      const t = safeTarget(target);
      if (!t) return;
      tl.to(t, vars, position);
    };

    if (!container) return;

    safeSet(title, { opacity: 0, y: 30 });
    safeSet(subtitle, { opacity: 0, y: 30 });
    safeSet(scrollIndicator, { opacity: 1 });
    safeSet(plane, { scale: 1, opacity: 1 });
    safeSet(sky, { opacity: 0, scale: 1.1 });
    safeSet(aboutLines, { opacity: 0, y: 20 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1,
        pin: stickyRef.current || undefined,
        pinSpacing: false,
      }
    });

    safeTo(title, { opacity: 1, y: 0, duration: 0.15 }, 0);
    safeTo(subtitle, { opacity: 1, y: 0, duration: 0.15 }, 0.05);
    safeTo(scrollIndicator, { opacity: 1, duration: 0.1 }, 0.05);

    safeTo([title, subtitle], { opacity: 0, y: -20, duration: 0.1 }, 0.15);
    safeTo(scrollIndicator, { opacity: 0, duration: 0.05 }, 0.15);

    safeTo(plane, {
      scale: 4,
      duration: 0.45,
      ease: 'power2.inOut'
    }, 0.25);

    if (aboutLines.length) {
      safeTo(aboutLines, {
        opacity: 1,
        y: 0,
        stagger: 0.18,
        duration: 0.45,
        ease: 'power3.out'
      }, 0.35);
    }

    safeTo(plane, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.out'
    }, 0.55);

    safeTo(sky, {
      opacity: 1,
      scale: 1,
      duration: 0.3,
      ease: 'power2.out'
    }, 0.55);

    safeTo(sky, {
      y: '-10%',
      duration: 0.15,
      ease: 'none'
    }, 0.85);

    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <section
      className="hero-scroll-container"
      id="home"
      ref={containerRef}
      style={{
        backgroundColor: 'transparent',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="hero-scroll-sticky" ref={stickyRef}>

        {/*
          Layer 2: Oblaci koji se pomeraju — nalaze se ISPOD window slike.
          Pozicionirani su tačno na mestu ovalnog prozora i animiraju se
          CSS keyframe animacijom s desna na levo u beskonačnoj petlji.
          Dva ista seta jedne pored druge = seamless loop.
        */}
        <div className="hero-window-clouds" />

        {/* Layer 3: window.webp — crni ram, ovalni prozor je providno staklo */}
        <div className="hero-layer-plane" ref={planeRef}>
          <img
            className="hero-window-bg"
            src="/images/hero.png"
            alt="Hero image inside airplane window"
          />
          <img
            className="hero-window-frame"
            src="/images/window.webp"
            alt="Airplane window"
          />
        </div>

        {/* Tekst */}
        <div className="hero-text-overlay">
          <h1 ref={titleRef}>{t.hero.title}</h1>
          <p ref={subtitleRef}>{t.hero.subtitle}</p>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator-hero" ref={scrollIndicatorRef}>
          <span>{t.hero.scroll}</span>
          <ChevronDown size={24} color="white" />
        </div>

      </div>
    </section>
  );
};

export default HeroScroll;
